import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Trans } from '@lingui/macro';
import { CircularProgress } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import {
  RETURN_TO_SESSION_KEY,
  useAuthState,
  useLogin,
  useLoginUsers,
  useLogout,
  useSelectedOrg,
  useUser,
} from 'features/auth/hooks/federatedAuthHooks';
import { CenterContainerFederated } from './CenterContainerFederated';
import { LoginFederated } from './LoginFederated';
import { SwitchUserFederated } from './SwitchUserFederated';
import { Text } from 'components/text/Text';
import { Button } from 'components/button/Button';

export const AuthLoaderFederated = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactNode => {
  const { isAuthenticated, isLoading } = useAuthState();
  const { selectedOrgAuth0Id, orgToken, clearSelectedOrg, isLoggingOut } = useSelectedOrg();
  const { error } = useAuth0();
  const login = useLogin();
  const logout = useLogout();
  // Fired only once orgToken is set (the hook gates on it). If Auth0 issued an org-scoped
  // token but the backend cannot resolve a user for that org (e.g. the environment's
  // Organization.Auth0OrgId backfill hasn't run, or the user has no row in that org), the
  // app must NOT mount the shell: every gated query would fail, spamming error toasts and
  // — via the legacy client's 401 authMonitor — bouncing through logout/auto-login (DL-61312).
  //
  // isPending must gate the shell too, not just isError: child queries (feature flags, sidebar
  // counts, etc.) only check `orgToken` before firing, not whether this lookup succeeded. Without
  // waiting for it to settle, the shell mounts for the render(s) between orgToken appearing and
  // useUser() resolving, those queries fire against an org the backend can't resolve, and their
  // error responses (a bare 401 that never reaches the app's JSON envelope, since it's rejected by
  // authentication middleware before any controller/exception-handler runs) surface as the
  // "Invalid error response" toast — even though the loader immediately replaces the shell with
  // the error screen a moment later (QA re-rejection on DL-61312, round 2).
  const { isError: isUserError, isPending: isUserPending } = useUser();

  // The set of D2-eligible login contexts for this Auth0 identity. `/api/users/me/logins` is
  // filtered server-side to designer orgs and supplier orgs with the portal enabled
  // (UserService.GetActiveApiUsers → .WhereSupplierPortalEnabled()), and — unlike /me — resolves
  // from the email claim alone, so it succeeds (200) even when the selected org's /me lookup 401s.
  // An EMPTY list therefore means this identity has no account D2 will ever admit; a NON-empty
  // list means only the *currently selected* org failed to resolve and the user can pick another.
  const { data: loginUsers, isPending: isLoginsPending } = useLoginUsers();

  // Distinguishes the two ways useUser() can error on the selected org:
  //  - identity has NO eligible login at all (empty list) → a legacy (non-portal) supplier who
  //    reached D2 with an org-scoped token for their Auth0-synced supplier org: /me rejects them
  //    (BearerTokenAuthenticationService → IsValidApiUser == false → 401). Per DL-60730 the gate
  //    is "only supplier PORTAL accounts may use D2"; such a user must be signed out, not offered
  //    a retry. Convergence with the 2506670 silent-empty-sidebar: a 401 here must actively log
  //    out, never silently render an empty shell.
  //  - identity HAS other eligible logins → only this org failed (e.g. Auth0OrgId backfill gap,
  //    DL-61312) → keep the "choose another organization" recovery screen below.
  const hasNoEligibleLogin = !isLoginsPending && loginUsers?.length === 0;

  // Guards a single auto-redirect. `useLogin()` returns a fresh function identity
  // every render and `login()` itself triggers state updates (query removal,
  // clearSelectedOrg, dismissNotification) that re-render this component, so
  // without the ref the effect would fire `loginWithRedirect` on every render —
  // the DL-61160 / React #185 redirect/render-loop class of bug.
  const redirectingRef = useRef(false);

  // Guards a single auto-logout for the non-portal-supplier gate (same reasoning as
  // redirectingRef): logout() triggers state updates that re-render this component, so without
  // the ref the effect would fire useLogout repeatedly.
  const loggingOutRef = useRef(false);

  // Deep-link restoration after the Auth0 round trip (DL-61837 follow-up). useLogin stashed the
  // path the logged-out user originally hit in sessionStorage; the SDK lands the callback on "/"
  // (redirect_uri is the origin), so without this the app default-routes and the deep link — e.g.
  // a /supplier/merge?mergeRequestId=… review link from an email — is lost. This lives here, not in
  // AuthProviderFederated, because that provider sits above <RouterProvider> and navigating from it
  // would require importing the router (a circular dependency the architecture gate rejects); this
  // component is inside the router, so useNavigate is available. Consumed exactly once.
  const navigate = useNavigate();
  const returnToConsumedRef = useRef(false);
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (returnToConsumedRef.current) return;
    const returnTo = sessionStorage.getItem(RETURN_TO_SESSION_KEY);
    if (!returnTo) return;
    returnToConsumedRef.current = true;
    sessionStorage.removeItem(RETURN_TO_SESSION_KEY);
    const current = `${window.location.pathname}${window.location.search}`;
    if (returnTo !== current) navigate(returnTo, { replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  useEffect(() => {
    // Do not auto-redirect while the SDK is still resolving, once authenticated,
    // or when Auth0 returned an error (otherwise an authenticated=false + error
    // state bounces the user straight back to Auth0 forever).
    if (isLoading || isAuthenticated || error) return;
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    void login();
  }, [isLoading, isAuthenticated, error, login]);

  useEffect(() => {
    // Non-portal supplier gate (DL-60730): the selected org's /me lookup failed AND this identity
    // has no D2-eligible login anywhere → sign out fully (Auth0 + legacy via useLogout) rather than
    // stranding them on the "choose another organization" screen for an org they can never load.
    // logout() ends in a top-level navigation that tears down this tree, so this fires once.
    if (isLoading || !isAuthenticated || isLoggingOut) return;
    if (!isUserError || !hasNoEligibleLogin) return;
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    void logout();
  }, [isLoading, isAuthenticated, isLoggingOut, isUserError, hasNoEligibleLogin, logout]);

  // Logout in flight (Slack report, DL-61837): useLogout clears the selected org while its
  // /betalogin/logout round trip is still running — without this branch the "authenticated +
  // no org" case below flashed the account selector for a few seconds mid-logout. Hold a plain
  // loader; the flow always ends in a top-level navigation that unloads this tree.
  if (isLoggingOut) {
    return (
      <Center>
        <CircularProgress />
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Center>
        <CircularProgress />
      </Center>
    );
  }

  if (!isAuthenticated) {
    // On an Auth0 callback error, fall back to the manual login page so the user
    // has a retry affordance and is not trapped in an auto-redirect bounce loop.
    if (error) {
      return <LoginFederated />;
    }

    // Otherwise the effect above is opening the Auth0 login page — show a spinner
    // instead of a "Log in" button while the redirect is in flight.
    return (
      <Center>
        <CircularProgress />
      </Center>
    );
  }

  if (!selectedOrgAuth0Id) {
    return <SwitchUserFederated />;
  }

  if (!orgToken || isUserPending) {
    return (
      <Center>
        <CircularProgress />
      </Center>
    );
  }

  if (isUserError) {
    // No eligible login → the auto-logout effect above is signing the user out; hold a loader
    // until the top-level logout navigation unmounts this tree (don't offer "choose another
    // organization" — there is none). A non-portal supplier lands here.
    if (hasNoEligibleLogin) {
      return (
        <Center>
          <CircularProgress />
        </Center>
      );
    }

    return (
      <CenterContainerFederated>
        <ErrorContainer>
          <Text variant="bodyMd" weight="semiBold" align="center">
            <Trans>We could not load your account for the selected organization.</Trans>
          </Text>
          <Text variant="bodySm" color="secondary" align="center">
            <Trans>
              Try another organization, or contact your organization administrator if the problem
              persists.
            </Trans>
          </Text>
          <ButtonGroup>
            <Button variant="primary" size="lg" onClick={clearSelectedOrg}>
              <Trans>Choose another organization</Trans>
            </Button>
            <Button variant="secondary" size="lg" onClick={logout}>
              <Trans>Back to login</Trans>
            </Button>
          </ButtonGroup>
        </ErrorContainer>
      </CenterContainerFederated>
    );
  }

  return children;
};

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${p => p.theme.s.md} ${p => p.theme.s.lg};
  gap: ${p => p.theme.s.lg};
  width: 100%;
`;

const ButtonGroup = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: ${p => p.theme.s.sm};
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background-color: ${p => p.theme.palette.bg.secondary};
`;
